/**
 * Постраничная загрузка данных отчета, общая для CSV и XLSX.
 * Параметры выгрузки не зависят от текущей страницы таблицы в интерфейсе.
 */
export const REPORT_PAGE_SIZE = 1000;
// Защитный лимит для памяти браузера и времени выгрузки, не ограничение формата XLSX.
export const MAX_REPORT_ROWS = 500000;

/**
 * Код ошибки позволяет интерфейсу различать отмену, некорректный ответ и превышение лимита.
 *
 * @param {String} code
 * @return {Error}
 */
function exportError(code) {
  const error = new Error(code);
  error.code = code;
  return error;
}

/**
 * Прерывает выгрузку, если пользователь запросил отмену.
 *
 * @param {Function} isCancelled Проверка текущего состояния отмены.
 */
export function checkReportExportCancelled(isCancelled) {
  if (isCancelled()) throw exportError("REPORT_EXPORT_CANCELLED");
}

/**
 * Последовательно загружает страницы и передает строки в обработчик без накопления всех данных.
 * Ошибка загрузки, нарушение пагинации или превышение лимита прерывают выгрузку,
 * чтобы вызывающий код не скачал неполный отчет как успешно сформированный файл.
 *
 * @param {Function} loadPage Загрузка по {page, pageSize}, возвращающая {rows, page: {all, current}}.
 * @param {Function} onRows Обработка строк страницы; может возвращать Promise.
 * @param {Function} isCancelled Проверка отмены до запроса и после асинхронных операций.
 * @param {Function} onProgress Получает {page, pages, rows} после обработки страницы.
 * @return {Promise.<void>}
 */
export async function forEachReportPage(loadPage, onRows, isCancelled = () => false, onProgress = () => {}) {
  let page = 1;
  let totalRows = 0;
  let allPages = 1;
  do {
    checkReportExportCancelled(isCancelled);
    const result = await loadPage({page, pageSize: REPORT_PAGE_SIZE});
    checkReportExportCancelled(isCancelled);
    if (result?.error) throw result.error;
    if (!Array.isArray(result?.rows) || !result.page) {
      throw exportError("REPORT_EXPORT_INVALID_RESPONSE");
    }
    // Некоторые API возвращают нулевое количество страниц для пустой выборки.
    allPages = Number(result.page.all);
    const currentPage = Number(result.page.current);
    if (!Number.isInteger(allPages) || allPages < 0
      || (allPages === 0 && result.rows.length > 0)
      || (currentPage !== page && !(page === 1 && currentPage === 0 && !result.rows.length))
      || (allPages > 0 && allPages < page)) {
      throw exportError("REPORT_EXPORT_INVALID_RESPONSE");
    }
    totalRows += result.rows.length;
    if (totalRows > MAX_REPORT_ROWS || (totalRows === MAX_REPORT_ROWS && page < allPages)) {
      throw exportError("REPORT_EXPORT_TOO_MANY_ROWS");
    }
    await onRows(result.rows);
    checkReportExportCancelled(isCancelled);
    onProgress({page, pages: Math.max(1, allPages), rows: totalRows});
    page += 1;
  } while (page <= allPages);
}

/**
 * Дает браузеру обновить индикатор и обработать отмену между порциями данных,
 * в том числе когда все строки отчета уже находятся в памяти.
 *
 * @return {Promise.<void>}
 */
export function yieldReportExport() {
  return new Promise(resolve => setTimeout(resolve, 0));
}
